import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { projectService, workspaceService, userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import { type ProjectFormData } from '@/lib/validations/project'
import { toast } from 'sonner'
import type { Workspace, User } from '@/types'
import { Status, Priority, Horizon } from '@/types/enums'
import { PageContainer, PageHeader } from '@/components/layout'
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog'
import { ProjectForm } from '@/components/projects/ProjectForm'

export default function ProjectCreatePage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [availableUsers, setAvailableUsers] = useState<User[]>([])
  const [isDirty, setIsDirty] = useState(false)

  // Track unsaved changes
  const unsavedChanges = useUnsavedChanges({
    isDirty,
  })

  const loadWorkspaces = useCallback(async () => {
    if (!user) return

    try {
      const allWorkspaces = await workspaceService.getAll()

      // Filter workspaces where user is creator or member
      const userWorkspaces = allWorkspaces.filter(
        (workspace) =>
          workspace.creator.id === user.id || workspace.users.some((u) => u.id === user.id)
      )

      setWorkspaces(userWorkspaces)
    } catch (error) {
      console.error('Failed to load workspaces:', error)
      toast.error('Failed to load workspaces')
    }
  }, [user])

  const loadUsers = useCallback(async () => {
    if (!user) return

    try {
      const allUsers = await userService.getAll()
      setAvailableUsers(allUsers)
    } catch (error) {
      console.error('Failed to load users:', error)
      toast.error('Failed to load users')
    }
  }, [user])

  useEffect(() => {
    loadWorkspaces()
    loadUsers()
  }, [loadWorkspaces, loadUsers])

  const onSubmit = async (data: ProjectFormData) => {
    if (!user) return

    setLoading(true)
    try {
      // Use createProject method which properly handles circular reference cleaning
      const newProject = await projectService.createProject(data, user.id)

      // Allow navigation before resetting dirty state and navigating
      unsavedChanges.allowNavigation()
      setIsDirty(false)

      toast.success('Project created successfully')
      navigate(`/projects/${newProject.id}`)
    } catch (error) {
      console.error('Failed to create project:', error)
      toast.error('Failed to create project')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader title="Create New Project" />

      <ProjectForm
        defaultValues={{
          status: Status.ToDo,
          priority: Priority.None,
          horizon: Horizon.None,
        }}
        workspaces={workspaces}
        availableUsers={availableUsers}
        selectedUsers={[]}
        onSubmit={onSubmit}
        onCancel={() => navigate('/projects')}
        submitText={loading ? 'Creating...' : 'Create Project'}
        loading={loading}
        title="Project Details"
        description="Fill in the details below to create a new project"
        onDirtyChange={setIsDirty}
      />

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={unsavedChanges.showDialog}
        onOpenChange={(open) => !open && unsavedChanges.handleCancelNavigation()}
        onConfirm={unsavedChanges.handleConfirmNavigation}
        onCancel={unsavedChanges.handleCancelNavigation}
      />
    </PageContainer>
  )
}
